import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
    createClient as createClientService,
    getClients,
    updateClientService,
    deleteClient as deleteClientService,
    getProjectsForClientModal,
} from './clients.service'

const initialState = {
    status: 'idle',
    clients: [],
    selectedClient: null,
    error: null,
    totalCount: 0,
    pageLimit: 10,
    currentPage: 1,
    skipPage: 0,
    isNext: false,
    clientPage: 1,
    clientSearch: '',
    // Client modal project list state
    clientModalProjectPage: 1,
    clientModalProjectSearch: '',
    clientModalProjectsListData: [],
    clientModalIsNext: false,
    clientModalListStatus: 'loaded',
}

// Async thunks
export const createClient = createAsyncThunk(
    'clients/create',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await createClientService(payload)
            return response
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

export const fetchClients = createAsyncThunk(
    'clients/fetchAll',
    async (payload = {}, { rejectWithValue }) => {
        try {
            const response = await getClients(payload)
            return response
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

export const updateClient = createAsyncThunk(
    'clients/update',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await updateClientService(id, data)
            return response
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

export const removeClient = createAsyncThunk(
    'clients/delete',
    async (id, { rejectWithValue }) => {
        try {
            const response = await deleteClientService(id)
            return response
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

// Fetch projects for client modal
export const fetchProjectsForClientModal = createAsyncThunk(
    'clients/fetchProjectsForModal',
    async (payload = {}, { rejectWithValue }) => {
        try {
            const response = await getProjectsForClientModal(payload)
            return response
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message)
        }
    }
)

const clientsSlice = createSlice({
    name: 'clients',
    initialState,
    reducers: {
        setSelectedClient: (state, action) => {
            state.selectedClient = action.payload
        },
        clearClients: (state) => {
            state.clients = []
            state.selectedClient = null
            state.clientPage = 1
            state.clientSearch = ''
            state.isNext = false
        },
        resetClients: (state) => {
            state.clients = []
            state.clientPage = 1
            state.clientSearch = ''
            state.isNext = false
        },
        setClientSearch: (state, action) => {
            state.clientSearch = action.payload
        },
        // Client modal project list actions
        resetClientModalProjects: (state) => {
            state.clientModalProjectsListData = []
            state.clientModalProjectPage = 1
            state.clientModalProjectSearch = ''
            state.clientModalIsNext = false
        },
        setClientModalProjectSearch: (state, action) => {
            state.clientModalProjectSearch = action.payload
            state.clientModalProjectPage = 1
            state.clientModalProjectsListData = []
        },
    },
    extraReducers: (builder) => {
        builder
            // Create client
            .addCase(createClient.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(createClient.fulfilled, (state, action) => {
                state.status = 'succeeded'
                if (action.payload?.data?.data) {
                    state.clients.unshift(action.payload.data.data)
                    state.totalCount += 1
                }
            })
            .addCase(createClient.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.payload
            })
            // Fetch clients
            .addCase(fetchClients.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(fetchClients.fulfilled, (state, action) => {
                state.status = 'loaded'
                const { skip = 0, search = '' } = action.meta.arg || {}
                const clientsData =
                    action.payload?.data?.result ||
                    action.payload?.data?.data ||
                    []

                // Check if this is a new search (skip === 0) or loading more
                if (skip === 0) {
                    // New search or initial load - replace data
                    state.clients = clientsData
                    state.clientPage = 1
                    state.clientSearch = search
                } else {
                    // Loading more - append data
                    state.clients = [...state.clients, ...clientsData]
                    state.clientPage = state.clientPage + 1
                }

                // Update isNext and totalCount
                state.isNext = action.payload?.data?.isNext ?? false
                if (action.payload?.data?.count !== undefined) {
                    state.totalCount = action.payload.data.count
                }
            })
            .addCase(fetchClients.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.payload
            })
            // Update client
            .addCase(updateClient.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(updateClient.fulfilled, (state, action) => {
                state.status = 'succeeded'
                const updatedClient = action.payload?.data?.data
                if (updatedClient) {
                    const index = state.clients.findIndex(
                        (c) => c._id === updatedClient._id
                    )
                    if (index !== -1) {
                        state.clients[index] = updatedClient
                    }
                }
            })
            .addCase(updateClient.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.payload
            })
            // Delete client
            .addCase(removeClient.pending, (state) => {
                state.status = 'loading'
            })
            .addCase(removeClient.fulfilled, (state, action) => {
                state.status = 'succeeded'
                const deletedId = action.payload?.data?.data?._id
                if (deletedId) {
                    state.clients = state.clients.filter(
                        (c) => c._id !== deletedId
                    )
                    state.totalCount -= 1
                }
            })
            .addCase(removeClient.rejected, (state, action) => {
                state.status = 'failed'
                state.error = action.payload
            })
            // Fetch projects for client modal
            .addCase(fetchProjectsForClientModal.pending, (state) => {
                state.clientModalListStatus = 'loading'
            })
            .addCase(fetchProjectsForClientModal.fulfilled, (state, action) => {
                state.clientModalListStatus = 'loaded'
                const { skip = 0, search = '' } = action.meta.arg || {}

                // Check if this is a new search (skip === 0) or loading more
                if (skip === 0) {
                    // New search or initial load - replace data
                    state.clientModalProjectsListData = action.payload.data
                    state.clientModalProjectPage = 1
                    state.clientModalProjectSearch = search
                } else {
                    // Loading more - append data
                    state.clientModalProjectsListData = [
                        ...state.clientModalProjectsListData,
                        ...action.payload.data,
                    ]
                    state.clientModalProjectPage =
                        state.clientModalProjectPage + 1
                }

                state.clientModalIsNext = action.payload.isNext
            })
            .addCase(fetchProjectsForClientModal.rejected, (state, action) => {
                state.clientModalListStatus = 'error'
                state.error = action.payload
            })
    },
})

export const {
    setSelectedClient,
    clearClients,
    resetClients,
    setClientSearch,
    resetClientModalProjects,
    setClientModalProjectSearch,
} = clientsSlice.actions
export const clientsState = (state) => state.clientsSlice || initialState

export default clientsSlice.reducer
